import { createApi } from '@reduxjs/toolkit/query/react';

import {
  DocumentCreationRequest,
  DocumentInstance,
} from '../types/letters-types';
import { baseQueryWithErrorHandling } from './baseQuery';

export const lettersApiClient = createApi({
  reducerPath: 'lettersApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    'Document',
    'DocumentInstances',
    'DocumentInstance',
    'DocumentPreview',
  ] as const,
  endpoints: (builder) => ({
    createDocument: builder.mutation<
      DocumentInstance,
      {
        matterId: string;
        data: DocumentCreationRequest & { coverage_id?: string };
      }
    >({
      query: ({ matterId, data }) => ({
        url: `api/matters/${matterId}/documents/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['DocumentInstances'],
    }),
    getMatterDocuments: builder.query<
      DocumentInstance[],
      { matterId: string; token?: string }
    >({
      query: ({ matterId, token }) =>
        `api/matters/${matterId}/documents/?token=${token}`,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'DocumentInstance' as const,
                id,
              })),
              'DocumentInstances',
            ]
          : ['DocumentInstances'],
    }),
    getDocumentInstance: builder.query<
      DocumentInstance,
      {
        matterId: string;
        documentInstanceId: string;
      }
    >({
      query: ({ matterId, documentInstanceId }) =>
        `api/matters/${matterId}/documents/${documentInstanceId}/`,
      providesTags: (result) =>
        result
          ? [{ type: 'DocumentInstance', id: result.id }]
          : ['DocumentInstance'],
    }),
    sendDocumentForSigning: builder.mutation<
      DocumentInstance,
      {
        matterId: string;
        documentInstanceId: string;
      }
    >({
      query: ({ matterId, documentInstanceId }) => ({
        url: `api/matters/${matterId}/documents/${documentInstanceId}/send/`,
        method: 'POST',
      }),
      invalidatesTags: (result) =>
        result
          ? [{ type: 'DocumentInstance', id: result.id }]
          : ['DocumentInstance'],
    }),
    resendDocumentForSigning: builder.mutation<
      DocumentInstance,
      {
        matterId: string;
        documentInstanceId: string;
      }
    >({
      query: ({ matterId, documentInstanceId }) => ({
        url: `api/matters/${matterId}/documents/${documentInstanceId}/resend/`,
        method: 'POST',
      }),
      invalidatesTags: (result) =>
        result
          ? [{ type: 'DocumentInstance', id: result.id }]
          : ['DocumentInstance'],
    }),
    refreshDocument: builder.mutation<
      DocumentInstance,
      { matterId: string; documentInstanceId: string }
    >({
      query: ({ matterId, documentInstanceId }) => ({
        url: `api/matters/${matterId}/documents/${documentInstanceId}/refresh/`,
        method: 'PATCH',
      }),
      invalidatesTags: (result) =>
        result
          ? [{ type: 'DocumentInstance', id: result.id }]
          : ['DocumentInstance'],
    }),
    downloadDocument: builder.query<
      Blob,
      { matterId: string; documentInstanceId: string }
    >({
      query: ({ matterId, documentInstanceId }) => ({
        url: `api/matters/${matterId}/documents/${documentInstanceId}/download/`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    deleteDocument: builder.mutation<
      void,
      { matterId: string; documentInstanceId: string }
    >({
      query: ({ matterId, documentInstanceId }) => ({
        url: `api/matters/${matterId}/documents/${documentInstanceId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DocumentInstances'],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useCreateDocumentMutation,
  useGetMatterDocumentsQuery,
  useLazyGetMatterDocumentsQuery,
  useGetDocumentInstanceQuery,
  useSendDocumentForSigningMutation,
  useResendDocumentForSigningMutation,
  useRefreshDocumentMutation,
  useDownloadDocumentQuery,
  useLazyDownloadDocumentQuery,
  useDeleteDocumentMutation,
} = lettersApiClient;
